import React, { useState } from 'react';

const AdminWaitlist = () => {
  const [password, setPassword] = useState('');
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState('');

  const loadWaitlist = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch('/api/waitlist-list?limit=500', {
        headers: {
          'x-admin-password': password
        }
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = data && data.error ? data.error : `Request failed (${response.status})`;
        throw new Error(message);
      }

      setEntries(Array.isArray(data.entries) ? data.entries : []);
      setTotal(typeof data.total === 'number' ? data.total : null);
      setLastUpdated(new Date().toLocaleString());
    } catch (err) {
      setError(err.message || 'Failed to load waitlist.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password) {
      setError('Password is required.');
      return;
    }
    loadWaitlist();
  };

  return (
    <section className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-light mb-4">
            Waitlist Admin
          </h1>
          <p className="text-gray-400 mb-10">
            Enter the admin password to view the latest signups.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4 mb-8">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="flex-1 px-4 py-3 rounded-xl bg-gray-900 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-primaryPurple"
            />
            <button
              type="submit"
              disabled={isLoading}
              className={`px-6 py-3 rounded-xl bg-gradient-to-r from-primaryPurple to-accentGold text-white font-medium transition-all ${
                isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:from-primaryPurple/90 hover:to-accentGold/90'
              }`}
            >
              {isLoading ? 'Loading...' : 'View Waitlist'}
            </button>
          </form>

          {error && (
            <div className="mb-6 text-red-400 text-sm">{error}</div>
          )}

          {(entries.length > 0 || total !== null) && (
            <div className="mb-6 text-gray-300 text-sm">
              <span className="font-medium text-white">{total ?? entries.length}</span> total signups
              {lastUpdated ? ` • Updated ${lastUpdated}` : ''}
            </div>
          )}

          <div className="bg-gray-900/60 border border-gray-800 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-800 text-sm text-gray-400 flex justify-between">
              <span>Email</span>
              <span>Signed up</span>
            </div>
            {entries.length === 0 ? (
              <div className="px-5 py-6 text-gray-500 text-sm">
                No entries yet.
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {entries.map((entry, index) => (
                  <div key={`${entry.email}-${index}`} className="px-5 py-4 flex justify-between text-sm">
                    <span className="text-gray-200">{entry.email}</span>
                    <span className="text-gray-400">
                      {entry.ts ? new Date(entry.ts).toLocaleString() : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {entries.length > 0 && (
            <button
              type="button"
              onClick={loadWaitlist}
              className="mt-6 text-gray-400 hover:text-white transition-colors duration-300 underline underline-offset-4"
            >
              Refresh list
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default AdminWaitlist;
